import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Button, IconButton, Alert, TextField, CircularProgress, Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { COLORS } from "../constants";
import { useRechargeApi } from "../hooks/useRechargeApi";
import type { AddressData } from "../types";

interface Props {
  open: boolean;
  onClose: () => void;
  address: AddressData | null;
  addressId: string | null;
  onSuccess: (msg: string) => void;
  onRefresh: () => void;
}

export function EditAddressModal({ open, onClose, address, addressId, onSuccess, onRefresh }: Props) {
  const { call } = useRechargeApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    address1: "",
    address2: "",
    city: "",
    province: "",
    zip: "",
    country: "",
    phone: "",
  });

  useEffect(() => {
    if (open && address) {
      setForm({
        first_name: address.first_name || "",
        last_name: address.last_name || "",
        address1: address.address1 || "",
        address2: address.address2 || "",
        city: address.city || "",
        province: address.province || "",
        zip: address.zip || "",
        country: address.country || "",
        phone: (address as any).phone || "",
      });
      setError(null);
    }
  }, [open, address]);

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async () => {
    if (!addressId) return;
    setLoading(true);
    setError(null);

    try {
      const res = await call("/recharge/address/update-fields", {
        method: "POST",
        body: { addressId, addressData: form },
      });
      if (!res.ok) throw new Error(res.error || "Failed to update address");
      onSuccess("Address updated successfully!");
      onClose();
      onRefresh();
    } catch (err: any) {
      setError(err.message || "Failed to update address");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography sx={{ fontWeight: 800, color: COLORS.brownDeep }}>Edit Address</Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2}>
            <TextField fullWidth label="First Name" value={form.first_name} onChange={handleChange("first_name")} size="small" />
            <TextField fullWidth label="Last Name" value={form.last_name} onChange={handleChange("last_name")} size="small" />
          </Stack>
          <TextField fullWidth label="Address Line 1" value={form.address1} onChange={handleChange("address1")} size="small" />
          <TextField fullWidth label="Address Line 2" value={form.address2} onChange={handleChange("address2")} size="small" />
          <Stack direction="row" spacing={2}>
            <TextField fullWidth label="City" value={form.city} onChange={handleChange("city")} size="small" />
            <TextField fullWidth label="County / Province" value={form.province} onChange={handleChange("province")} size="small" />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField fullWidth label="Postcode / ZIP" value={form.zip} onChange={handleChange("zip")} size="small" />
            <TextField fullWidth label="Country" value={form.country} onChange={handleChange("country")} size="small" />
          </Stack>
          <TextField fullWidth label="Phone" value={form.phone} onChange={handleChange("phone")} size="small" />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading || !form.address1}
          sx={{ bgcolor: COLORS.brown, "&:hover": { bgcolor: COLORS.brownDark }, textTransform: "none" }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : "Save Address"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
